
const libPictView = require('pict-view');
require('../approval-workflow/ApprovalWorkflow-Help.js'); // sets window.ApprovalWorkflowHelp for the (i) icons

/**
 * ApprovalWorkflowBindings — the #/ApprovalWorkflows/Bindings page.
 *
 * Maps each document type (ReportNamedInstance) to the approval workflow that governs it. Reads/writes the
 * Configuration namespace 'ApprovalWorkflowBindings' (NamespaceValue 'Document') as a flat
 * { documentHash: workflowHash } map. A synthetic "Default" row is the fallback for unbound documents. All
 * data lives in ApprovalWorkflowProvider.bindingsState; this view derives the render rows + repaints.
 *
 * SCOPE. The screen edits two levels of that config (mirroring Home): the customer-wide DEFAULT set, and one
 * PROJECT's overrides. A project inherits every customer default unless it sets its own binding, so a project
 * can diverge from the default per document type. The scope bar switches between the two; in project scope each
 * row's dropdown gains a "use customer default" choice and rows that diverge are flagged.
 */
class ApprovalWorkflowBindings extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.ApprovalWorkflow;
	}

	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	get bindingsState()
	{
		return this.provider ? this.provider.bindingsState : { Loading: false, Error: '', Rows: [], WorkflowHashes: [], Map: {}, CustomerMap: {}, Dirty: false, Editable: false, Scope: 'Customer', IDProject: 0, Projects: [] };
	}

	/**
	 * Build the per-row render records for the current scope. In customer scope a row's dropdown is
	 * "— None — + every workflow"; in project scope it is "use customer default + every workflow + no-approval
	 * override", and the row is tagged inherited / overrides-default / opt-out relative to the customer set.
	 */
	_buildRenderRows()
	{
		const tmpState = this.bindingsState;
		const tmpProject = (tmpState.Scope === 'Project');
		const tmpWorkflowHashes = tmpState.WorkflowHashes || [];
		const tmpInherit = this.provider ? this.provider.BINDING_INHERIT : '__INHERIT__';
		const tmpNone = this.provider ? this.provider.BINDING_NONE : '__NONE__';
		const tmpNoun = tmpState.TargetNoun || 'document type';
		return (tmpState.Rows || []).map((pRow) =>
		{
			const tmpCurrent = tmpState.Map[pRow.DocHash] || '';
			const tmpDefault = (tmpState.CustomerMap || {})[pRow.DocHash] || '';
			const tmpBound = (tmpCurrent !== '');
			const tmpDiverged = tmpProject && (tmpCurrent !== tmpDefault);
			const tmpOverridden = tmpDiverged && tmpBound;   // project overrides the default with a specific workflow
			const tmpOptedOut = tmpDiverged && !tmpBound;    // project overrides the default with "no approval"
			const tmpInherited = tmpProject && !tmpDiverged; // project takes the customer default as-is

			let tmpOptions;
			if (tmpProject)
			{
				const tmpDefaultLabel = tmpDefault ? tmpDefault : 'no approval';
				tmpOptions = [ { Hash: tmpInherit, Label: `Use customer default (${tmpDefaultLabel})`, SelectedAttr: tmpInherited ? 'selected' : '' } ];
				tmpWorkflowHashes.forEach((pHash) => { tmpOptions.push({ Hash: pHash, Label: pHash, SelectedAttr: (tmpOverridden && tmpCurrent === pHash) ? 'selected' : '' }); });
				// An override pointing at a workflow that no longer exists still needs to show as selected.
				if (tmpOverridden && tmpWorkflowHashes.indexOf(tmpCurrent) < 0)
				{
					tmpOptions.push({ Hash: tmpCurrent, Label: `${tmpCurrent} (missing)`, SelectedAttr: 'selected' });
				}
				// Opt-out only makes sense when there IS a customer default to override.
				if (tmpDefault)
				{
					tmpOptions.push({ Hash: tmpNone, Label: '— No approval (override) —', SelectedAttr: tmpOptedOut ? 'selected' : '' });
				}
			}
			else
			{
				tmpOptions = [ { Hash: '', Label: '— None —', SelectedAttr: tmpCurrent ? '' : 'selected' } ]
					.concat(tmpWorkflowHashes.map((pHash) => ({ Hash: pHash, Label: pHash, SelectedAttr: (tmpCurrent === pHash) ? 'selected' : '' })));
				if (tmpCurrent && tmpWorkflowHashes.indexOf(tmpCurrent) < 0)
				{
					tmpOptions.push({ Hash: tmpCurrent, Label: `${tmpCurrent} (missing)`, SelectedAttr: 'selected' });
				}
			}

			return {
				DocHash: pRow.DocHash,
				DocName: pRow.DocName,
				Filter: `${pRow.DocName} ${pRow.DocHash}`.toLowerCase(),
				RowClass: (tmpBound ? 'is-bound' : 'is-unbound')
					+ (pRow.Synthetic ? ' is-default' : '')
					+ (tmpOverridden ? ' is-override' : '')
					+ (tmpOptedOut ? ' is-optout' : '')
					+ (tmpInherited ? ' is-inherited' : ''),
				DotTitle: this._dotTitle(tmpProject, tmpBound, tmpCurrent, tmpDefault, tmpOverridden, tmpOptedOut, tmpInherited, tmpNoun),
				HashSlot: (pRow.DocName !== pRow.DocHash && !pRow.Synthetic) ? [ { DocHash: pRow.DocHash } ] : [],
				SyntheticSlot: pRow.Synthetic ? [ {} ] : [],
				OrphanSlot: pRow.Orphan ? [ {} ] : [],
				OverrideSlot: tmpOverridden ? [ {} ] : [],
				OptOutSlot: tmpOptedOut ? [ {} ] : [],
				// "Inherited" tag only when there is an actual default value being taken (not for inherited-none).
				InheritedSlot: (tmpInherited && tmpDefault) ? [ {} ] : [],
				Options: tmpOptions,
				Disabled: tmpState.Editable ? '' : 'disabled',
			};
		});
	}

	/**
	 * The header summary slot ("N / M governed · K overriding default") derived from the render rows. Empty
	 * while loading / erroring / choosing a project / when there's nothing but the Default row.
	 */
	_summarySlot(pRenderRows)
	{
		const tmpState = this.bindingsState;
		const tmpNoProjectChosen = (tmpState.Scope === 'Project') && !(tmpState.IDProject > 0);
		if (tmpState.Loading || tmpState.Error || tmpNoProjectChosen || (tmpState.Rows || []).length <= 1)
		{
			return [];
		}
		let tmpBound = 0;
		let tmpOverrides = 0;
		pRenderRows.forEach((pRow) =>
		{
			if (pRow.RowClass.indexOf('is-bound') > -1) { tmpBound++; }
			if (pRow.RowClass.indexOf('is-override') > -1 || pRow.RowClass.indexOf('is-optout') > -1) { tmpOverrides++; }
		});
		return [ { Bound: tmpBound, Total: (tmpState.Rows || []).length, OverrideSlot: (tmpState.Scope === 'Project' && tmpOverrides > 0) ? [ { Count: tmpOverrides } ] : [] } ];
	}

	/** Repaint the header summary region (its own region so an in-row edit keeps the count in sync). */
	_paintSummary(pRenderRows)
	{
		if (!this.pict.ContentAssignment.getElement('#ApprovalWorkflowBindings-Summary')) { return; }
		const tmpSlot = this._summarySlot(pRenderRows);
		this.pict.AppData.ApprovalWorkflowEditor.BindingsSummarySlot = tmpSlot;
		this.pict.ContentAssignment.assignContent('#ApprovalWorkflowBindings-Summary', this.pict.parseTemplateByHash('ApprovalWorkflow-Bindings-SummaryRegion', { Slot: tmpSlot }));
	}

	/** The green-dot tooltip text for a row, spelling out where its governing workflow comes from. (No quote
	 *  characters around the workflow name — the template renderer truncates the value at a smart quote.) */
	_dotTitle(pProject, pBound, pCurrent, pDefault, pOverridden, pOptedOut, pInherited, pNoun)
	{
		if (pProject)
		{
			if (pOverridden) { return `Overrides the customer default — governed by ${pCurrent}`; }
			if (pOptedOut) { return `Overrides the customer default — no approval workflow`; }
			if (pInherited && pBound) { return `Inherited from the customer default — governed by ${pCurrent}`; }
			return `Inherited from the customer default — no approval workflow`;
		}
		return pBound ? `Governed by ${pCurrent}` : `No approval workflow — this ${pNoun} is not governed`;
	}

	onBeforeRender(pRenderable)
	{
		const tmpState = this.bindingsState;
		const tmpApp = this.pict.AppData.ApprovalWorkflowEditor || {};
		const tmpProject = (tmpState.Scope === 'Project');
		const tmpNoProjectChosen = tmpProject && !(tmpState.IDProject > 0);

		const tmpRenderRows = this._buildRenderRows();
		tmpApp.BindingsRows = tmpRenderRows;

		tmpApp.BindingsLoadingSlot = tmpState.Loading ? [ {} ] : [];
		tmpApp.BindingsScopeLoadingSlot = tmpState.ScopeLoading ? [ {} ] : [];
		tmpApp.BindingsErrorSlot = tmpState.Error ? [ { Message: tmpState.Error } ] : [];
		tmpApp.BindingsChooseProjectSlot = tmpNoProjectChosen ? [ {} ] : [];
		tmpApp.BindingsEmptySlot = (!tmpState.Loading && !tmpState.Error && !tmpState.ScopeLoading && !tmpNoProjectChosen && (tmpState.Rows || []).length <= 1) ? [ {} ] : [];
		tmpApp.BindingsSummarySlot = this._summarySlot(tmpRenderRows);

		// Scope bar (customer default vs a specific project).
		tmpApp.BindingsScopeCustomerClass = tmpProject ? '' : 'is-link is-selected';
		tmpApp.BindingsScopeProjectClass = tmpProject ? 'is-link is-selected' : '';
		// The project scope picker is a searchable Pict-Section-Picker mounted in onAfterRender (server-side search);
		// the slot just gates whether its mount is present (project scope only).
		tmpApp.BindingsProjectSelectSlot = tmpProject ? [ {} ] : [];
		tmpApp.BindingsScopeNote = tmpProject
			? (tmpNoProjectChosen
				? `Choose a project to view or override its ${tmpState.TargetNounPlural || 'document types'}.`
				: `Overriding bindings for ${tmpState.ProjectName || 'this project'}. Rows left on the customer default inherit the customer set; changing one makes it diverge for this project only.`)
			: `These are the customer-wide defaults. Every project inherits them unless it sets its own override below.`;
		// The dot legend gains its override entry only in project scope.
		tmpApp.BindingsLegendProjectSlot = tmpProject ? [ {} ] : [];

		// Family-aware labels (Document vs Observation).
		const tmpNoun = tmpState.TargetNoun || 'document type';
		tmpApp.BindingsTitle = tmpState.Title || 'Document Bindings';
		tmpApp.BindingsTargetNoun = tmpNoun;
		tmpApp.BindingsTargetNounPlural = tmpState.TargetNounPlural || 'document types';
		tmpApp.BindingsTargetHeader = tmpNoun.charAt(0).toUpperCase() + tmpNoun.slice(1);
		tmpApp.BindingsServerPendingSlot = tmpState.ServerPending ? [ {} ] : [];
		return super.onBeforeRender(pRenderable);
	}

	/** Client-side filter of the binding rows by document name/hash (show/hide, no re-render). */
	filterRows(pQuery)
	{
		const tmpQuery = String(pQuery || '').trim().toLowerCase();
		let tmpVisible = 0;
		Array.prototype.forEach.call(document.querySelectorAll('.apwb-row'), (pRow) =>
		{
			const tmpText = pRow.getAttribute('data-filter') || '';
			const tmpMatch = !tmpQuery || tmpText.indexOf(tmpQuery) > -1;
			pRow.style.display = tmpMatch ? '' : 'none';
			if (tmpMatch) { tmpVisible++; }
		});
		const tmpEmpty = document.getElementById('ApprovalWorkflowBindings-NoMatch');
		if (tmpEmpty) { tmpEmpty.style.display = (tmpVisible === 0 && tmpQuery) ? '' : 'none'; }
	}

	/** Re-apply the current filter box to freshly rendered rows (rows are replaced on edit/scope change). */
	_reapplyFilter()
	{
		const tmpInput = document.getElementById('ApprovalWorkflowBindings-Filter');
		if (tmpInput && tmpInput.value) { this.filterRows(tmpInput.value); }
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this._renderActions();
		this.pict.CSSMap.injectCSS();
		// Mount the searchable project-scope picker (present only in project scope; the method no-ops otherwise).
		if (this.provider) { this.provider.mountBindingProjectPicker(); }
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	/** The Save button + dirty badge (its own region so a dropdown change doesn't re-render the whole table). */
	_renderActions()
	{
		if (!this.pict.ContentAssignment.getElement('#ApprovalWorkflowBindings-Actions')) { return; }
		const tmpState = this.bindingsState;
		const tmpBlockedReason = (tmpState.Editable && this.provider) ? this.provider.bindingsSaveBlockedReason() : '';
		const tmpSaveLabel = (tmpState.Scope === 'Project') ? 'Save project overrides' : 'Save customer defaults';
		const tmpRecord =
		{
			EditSlot: (tmpState.Editable && !tmpBlockedReason) ? [ {} ] : [],
			BlockedSlot: (tmpState.Editable && tmpBlockedReason) ? [ { Reason: tmpBlockedReason, SaveLabel: tmpSaveLabel } ] : [],
			ReadOnlySlot: tmpState.Editable ? [] : [ {} ],
			DirtySlot: (tmpState.Editable && tmpState.Dirty) ? [ {} ] : [],
			SaveLabel: tmpSaveLabel,
		};
		this.pict.ContentAssignment.assignContent('#ApprovalWorkflowBindings-Actions', this.pict.parseTemplateByHash('ApprovalWorkflowBindings-Actions-Template', tmpRecord));
	}

	/** Re-render just the table rows (keeps the filter box + scope bar intact) so edits update the flags live. */
	_renderRows()
	{
		if (!this.pict.ContentAssignment.getElement('#ApprovalWorkflowBindings-Rows')) { return; }
		const tmpRows = this._buildRenderRows();
		this.pict.AppData.ApprovalWorkflowEditor.BindingsRows = tmpRows;
		this.pict.ContentAssignment.assignContent('#ApprovalWorkflowBindings-Rows', this.pict.parseTemplateByHash('ApprovalWorkflow-Bindings-RowSet', { Rows: tmpRows }));
		this._reapplyFilter();
		// Keep the header count in sync — the summary lives outside the table region.
		this._paintSummary(tmpRows);
	}

	onBindingChange(pDocHash, pValue)
	{
		this.provider.setBinding(pDocHash, pValue);
		this._renderRows();     // refresh inherited / overrides-default flags for the changed row
		this._renderActions();  // surface the dirty badge
	}

	// --- Scope switching (customer default vs a specific project) ---------------------------------------

	/** Guard scope changes behind a confirm when there are unsaved edits. @return {Promise<Boolean>} proceed? */
	async _confirmLoseEdits()
	{
		if (!this.bindingsState.Dirty) { return true; }
		return await this.modal.confirm('Discard your unsaved binding changes?',
			{ title: 'Unsaved changes', confirmLabel: 'Discard', cancelLabel: 'Keep editing', dangerous: true });
	}

	async onScopeChange(pScope)
	{
		const tmpState = this.bindingsState;
		if (pScope === tmpState.Scope) { return; }
		if (!(await this._confirmLoseEdits())) { return; }
		if (pScope === 'Project')
		{
			// Project scope uses a server-side SEARCH picker now — there is no preloaded Projects list to check
			// or default from (the old capped `Projects/Lite/…/0/1000` <select> was replaced by
			// mountBindingProjectPicker). Switch to Project scope reusing the last-chosen project if there is
			// one, else with no selection: the provider then shows the "pick a project" prompt and the picker
			// (onProjectChange) drives the actual selection. The stale `tmpState.Projects.length` guard here was
			// firing "No projects found" on every click because that array is never populated any more.
			await this.provider.switchBindingScopeAsync('Project', tmpState.IDProject || 0);
		}
		else
		{
			await this.provider.switchBindingScopeAsync('Customer', 0);
		}
	}

	async onProjectChange(pIDProject)
	{
		if (!(await this._confirmLoseEdits())) { this.render(); return; } // re-render to restore the select
		if (!pIDProject) { return; }
		await this.provider.switchBindingScopeAsync('Project', pIDProject);
	}

	async saveBindings()
	{
		try
		{
			const tmpResult = await this.provider.saveBindingsAsync();
			this._renderActions();
			this._renderRows();
			if (tmpResult.Scope === 'Project')
			{
				const tmpOverrides = tmpResult.OverrideCount || 0;
				this.modal.toast(`Saved. ${tmpOverrides} ${pluralizeCount(tmpOverrides, 'override', 'overrides')} for ${this.bindingsState.ProjectName || 'this project'}; the rest inherit the customer default.`, { type: 'success' });
			}
			else
			{
				this.modal.toast(`Saved ${tmpResult.Count} customer default binding${tmpResult.Count === 1 ? '' : 's'}.`, { type: 'success' });
			}
		}
		catch (pError)
		{
			this.modal.toast(`Save failed: ${pError && pError.message ? pError.message : pError}`, { type: 'error', duration: 6000 });
		}
	}
}

/** Tiny local pluralizer for the save toast. */
function pluralizeCount(pCount, pSingular, pPlural)
{
	return (Number(pCount) === 1) ? pSingular : pPlural;
}

module.exports = ApprovalWorkflowBindings;

module.exports.default_configuration =
{
	ViewIdentifier: 'ApprovalWorkflowBindings',
	DefaultRenderable: 'ApprovalWorkflowBindingsWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.apwb-wrap { padding: 1.25rem 1.5rem 2rem; max-width: 66rem; }
		.apwb-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 0.5rem; }
		.apwb-actions { display: flex; align-items: center; gap: 0.6rem; }
		.apwb-dirty { display: inline-flex; align-items: center; gap: 0.35rem; color: #b8860b; font-size: 0.78rem; font-weight: 600; }
		.apwb-dirty .icon { font-size: 0.5rem; animation: apwb-pulse 1.6s ease-in-out infinite; }
		@keyframes apwb-pulse { 50% { opacity: 0.35; } }
		.apwb-badge { display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.75rem; padding: 0.2rem 0.6rem; border-radius: 999px; background: var(--bulma-scheme-main-bis, #f2f4f8); color: var(--bulma-text-weak, #7a7a7a); border: 1px solid var(--bulma-border, #e6e9ef); }
		.apwb-summary { display: inline-flex; align-items: center; font-size: 0.72rem; font-weight: 700; color: var(--bulma-link, #156dd1); background: #eef3fb; border-radius: 999px; padding: 0.1rem 0.6rem; margin-left: 0.5rem; vertical-align: middle; }
		.apwb-summary .apwb-summary-ovr { color: #8a4b16; }

		/* --- Scope bar: customer default vs a specific project. --- */
		.apwb-scopebar { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; margin: 0.9rem 0 0.35rem; padding: 0.6rem 0.75rem; border: 1px solid var(--bulma-border, #e6e9ef); border-radius: 10px; background: var(--bulma-scheme-main-bis, #f7f9fc); }
		.apwb-scope-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--bulma-text-weak, #9aa3b2); }
		.apwb-scopebar .buttons { margin-bottom: 0; }
		.apwb-scopebar .buttons .button { margin-bottom: 0; }
		.apwb-scope-project { display: flex; align-items: center; gap: 0.4rem; }
		.apwb-scope-note { flex-basis: 100%; font-size: 0.76rem; color: var(--bulma-text-weak, #6b7484); margin-top: 0.1rem; }
		.apwb-scope-note strong { color: #4a5568; }

		.apwb-toolbar { margin: 0.85rem 0 0.6rem; max-width: 24rem; }

		/* --- Dot legend (explains the status dots). --- */
		.apwb-legend { display: flex; align-items: center; flex-wrap: wrap; gap: 0.35rem 1.1rem; padding: 0.5rem 0.2rem 0.75rem; font-size: 0.74rem; color: var(--bulma-text-weak, #6b7484); }
		.apwb-legend-item { display: inline-flex; align-items: center; gap: 0.4rem; }
		.apwb-legend-note { font-style: italic; }
		.apwb-legend .apwb-dot { position: static; }
		.apwb-dot.legend-bound { background: #2e9e5b; border-color: #2e9e5b; }
		.apwb-dot.legend-override { background: #2e9e5b; border-color: #156dd1; box-shadow: 0 0 0 3px rgba(21,109,209,0.22); }

		.apwb-table { width: 100%; border-collapse: collapse; }
		.apwb-table th { text-align: left; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--bulma-text-weak, #9aa3b2); padding: 0.5rem 0.7rem; border-bottom: 2px solid var(--bulma-border, #e6e9ef); }
		.apwb-table td { padding: 0.55rem 0.7rem; border-bottom: 1px solid var(--bulma-border, #f0f2f5); vertical-align: middle; }
		.apwb-row { transition: background 0.1s; }
		.apwb-row:hover { background: var(--bulma-scheme-main-bis, #f6f8fc); }
		.apwb-row.is-default { background: #f5f8fe; }
		.apwb-row.is-default:hover { background: #eef3fb; }
		.apwb-name-cell { display: flex; align-items: baseline; gap: 0.5rem; }
		.apwb-dot { flex-shrink: 0; align-self: center; width: 0.55rem; height: 0.55rem; border-radius: 50%; border: 1.5px solid var(--bulma-border, #c7cdd8); background: transparent; }
		.apwb-row.is-bound .apwb-dot { background: #2e9e5b; border-color: #2e9e5b; box-shadow: 0 0 0 3px rgba(46,158,91,0.14); }
		/* Project scope: a diverging row rings its dot in the accent colour so overrides stand out from inherited rows. */
		.apwb-row.is-override .apwb-dot { border-color: #156dd1; box-shadow: 0 0 0 3px rgba(21,109,209,0.20); }
		.apwb-row.is-optout .apwb-dot { background: transparent; border-color: #b26a2c; box-shadow: 0 0 0 3px rgba(178,106,44,0.18); }
		.apwb-row.is-inherited .apwb-doc-name { color: #48566b; }
		.apwb-doc-name { font-weight: 600; color: #22334c; }
		.apwb-doc-hash { font-size: 0.74rem; color: var(--bulma-text-weak, #8a93a3); margin-left: 1.05rem; }
		.apwb-tag { font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; padding: 0.08rem 0.45rem; border-radius: 999px; margin-left: 0.4rem; }
		.apwb-tag.default { background: #e7eefb; color: #3a5a9b; }
		.apwb-tag.orphan { background: #fdeee6; color: #b26a2c; }
		.apwb-tag.override { background: #e6f0fd; color: #1c5fb0; }
		.apwb-tag.optout { background: #fdeee6; color: #b26a2c; }
		.apwb-tag.inherited { background: #eef0f3; color: #6b7484; text-transform: none; font-weight: 600; }
		.apwb-select-cell { width: 42%; min-width: 15rem; }
		.apwb-empty { padding: 2.5rem 1rem; }
		.apwb-spinner { display: inline-block; width: 1.6rem; height: 1.6rem; border: 3px solid var(--bulma-border, #dbdbdb); border-top-color: var(--bulma-link, #156dd1); border-radius: 50%; animation: apwb-spin 0.8s linear infinite; }
		@keyframes apwb-spin { to { transform: rotate(360deg); } }
	`,

	Templates:
	[
		{
			Hash: 'ApprovalWorkflow-Bindings-Wrap',
			Template: /*html*/`
				<div class="apwb-wrap">
					<div class="apwb-header">
						<div>
							<div class="apwe-crumbs" style="font-size:0.85rem;margin-bottom:0.15rem;"><a href="#/ApprovalWorkflows" style="color:var(--bulma-link,#156dd1)"><span class="icon is-small"><i class="fas fa-chevron-left"></i></span> Approval Workflows</a></div>
							<h1 class="title is-4"><span class="icon"><i class="fas fa-link"></i></span> {~D:AppData.ApprovalWorkflowEditor.BindingsTitle~}<span id="ApprovalWorkflowBindings-Summary">{~TS:ApprovalWorkflow-Bindings-Summary:AppData.ApprovalWorkflowEditor.BindingsSummarySlot~}</span> <span class="apw-help" role="button" tabindex="0" title="What are bindings?" onclick="ApprovalWorkflowHelp.show('bindings.overview', this)"><i class="fas fa-circle-info"></i></span></h1>
							<p class="subtitle is-6 has-text-grey">Choose which approval workflow governs each {~D:AppData.ApprovalWorkflowEditor.BindingsTargetNoun~}. The <strong>Default</strong> binding applies to any {~D:AppData.ApprovalWorkflowEditor.BindingsTargetNoun~} without its own.</p>
						</div>
						<div class="apwb-actions" id="ApprovalWorkflowBindings-Actions"></div>
					</div>

					<div class="apwb-scopebar">
						<span class="apwb-scope-label">Scope <span class="apw-help" role="button" tabindex="0" title="About binding scope" onclick="ApprovalWorkflowHelp.show('bindings.scope', this)"><i class="fas fa-circle-info"></i></span></span>
						<div class="buttons has-addons">
							<button class="button is-small {~D:AppData.ApprovalWorkflowEditor.BindingsScopeCustomerClass~}" onclick="_Pict.views.ApprovalWorkflowBindings.onScopeChange('Customer')"><span class="icon is-small"><i class="fas fa-building"></i></span><span>Customer default</span></button>
							<button class="button is-small {~D:AppData.ApprovalWorkflowEditor.BindingsScopeProjectClass~}" onclick="_Pict.views.ApprovalWorkflowBindings.onScopeChange('Project')"><span class="icon is-small"><i class="fas fa-folder-open"></i></span><span>Specific project</span></button>
						</div>
						{~TS:ApprovalWorkflow-Bindings-ProjectSelect:AppData.ApprovalWorkflowEditor.BindingsProjectSelectSlot~}
						<div class="apwb-scope-note">{~D:AppData.ApprovalWorkflowEditor.BindingsScopeNote~}</div>
					</div>

					{~TS:ApprovalWorkflow-Bindings-ServerPending:AppData.ApprovalWorkflowEditor.BindingsServerPendingSlot~}
					{~TS:ApprovalWorkflow-Bindings-Error:AppData.ApprovalWorkflowEditor.BindingsErrorSlot~}

					<div class="apwb-toolbar">
						<div class="control has-icons-left">
							<input class="input" type="text" id="ApprovalWorkflowBindings-Filter" placeholder="Filter {~D:AppData.ApprovalWorkflowEditor.BindingsTargetNounPlural~}…" oninput="_Pict.views.ApprovalWorkflowBindings.filterRows(this.value)">
							<span class="icon is-left"><i class="fas fa-magnifying-glass"></i></span>
						</div>
					</div>

					<div class="box">
						{~TS:ApprovalWorkflow-Bindings-Loading:AppData.ApprovalWorkflowEditor.BindingsLoadingSlot~}
						{~TS:ApprovalWorkflow-Bindings-ScopeLoading:AppData.ApprovalWorkflowEditor.BindingsScopeLoadingSlot~}
						{~TS:ApprovalWorkflow-Bindings-ChooseProject:AppData.ApprovalWorkflowEditor.BindingsChooseProjectSlot~}
						<div class="apwb-legend">
							<span class="apwb-legend-item"><span class="apwb-dot legend-bound"></span> Governed by a workflow</span>
							<span class="apwb-legend-item"><span class="apwb-dot"></span> Not governed</span>
							{~TS:ApprovalWorkflow-Bindings-LegendProject:AppData.ApprovalWorkflowEditor.BindingsLegendProjectSlot~}
							<span class="apw-help" role="button" tabindex="0" title="About the status dots" onclick="ApprovalWorkflowHelp.show('bindings.dots', this)"><i class="fas fa-circle-info"></i></span>
						</div>
						<table class="apwb-table">
							<thead><tr><th>{~D:AppData.ApprovalWorkflowEditor.BindingsTargetHeader~}</th><th class="apwb-select-cell">Approval workflow</th></tr></thead>
							<tbody id="ApprovalWorkflowBindings-Rows">{~TS:ApprovalWorkflow-Bindings-Row:AppData.ApprovalWorkflowEditor.BindingsRows~}</tbody>
						</table>
						<div id="ApprovalWorkflowBindings-NoMatch" class="apwb-empty has-text-grey has-text-centered" style="display:none"><span class="icon is-large"><i class="fas fa-magnifying-glass fa-lg"></i></span><p class="mt-2">No {~D:AppData.ApprovalWorkflowEditor.BindingsTargetNounPlural~} match your filter.</p></div>
						{~TS:ApprovalWorkflow-Bindings-Empty:AppData.ApprovalWorkflowEditor.BindingsEmptySlot~}
					</div>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-ProjectSelect',
			Template: /*html*/`
				<div class="apwb-scope-project">
					<div id="ApprovalWorkflowBindings-ProjectPicker" class="apwb-project-picker"></div>
				</div>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-ProjectOption',
			Template: /*html*/`<option value="{~D:Record.IDProject~}" {~D:Record.SelectedAttr~}>{~D:Record.Name~}</option>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-LegendProject',
			Template: /*html*/`
				<span class="apwb-legend-item"><span class="apwb-dot legend-override"></span> Overrides the customer default</span>
				<span class="apwb-legend-item apwb-legend-note">Other rows inherit the customer default</span>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-SummaryRegion',
			Template: /*html*/`{~TS:ApprovalWorkflow-Bindings-Summary:Record.Slot~}`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-Summary',
			Template: /*html*/`<span class="apwb-summary">{~D:Record.Bound~} / {~D:Record.Total~} governed{~TS:ApprovalWorkflow-Bindings-Summary-Ovr:Record.OverrideSlot~}</span>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-Summary-Ovr',
			Template: /*html*/`<span class="apwb-summary-ovr">&nbsp;· {~D:Record.Count~} overriding default</span>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-RowSet',
			Template: /*html*/`{~TS:ApprovalWorkflow-Bindings-Row:Record.Rows~}`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-Row',
			Template: /*html*/`
				<tr class="apwb-row {~D:Record.RowClass~}" data-filter="{~D:Record.Filter~}">
					<td>
						<div class="apwb-name-cell">
							<span class="apwb-dot" title="{~D:Record.DotTitle~}"></span>
							<span class="apwb-doc-name">{~D:Record.DocName~}</span>{~TS:ApprovalWorkflow-Bindings-DefaultTag:Record.SyntheticSlot~}{~TS:ApprovalWorkflow-Bindings-OverrideTag:Record.OverrideSlot~}{~TS:ApprovalWorkflow-Bindings-OptOutTag:Record.OptOutSlot~}{~TS:ApprovalWorkflow-Bindings-InheritedTag:Record.InheritedSlot~}{~TS:ApprovalWorkflow-Bindings-OrphanTag:Record.OrphanSlot~}
						</div>
						{~TS:ApprovalWorkflow-Bindings-Hash:Record.HashSlot~}
					</td>
					<td class="apwb-select-cell">
						<div class="select is-small is-fullwidth">
							<select {~D:Record.Disabled~} onchange="_Pict.views.ApprovalWorkflowBindings.onBindingChange('{~D:Record.DocHash~}', this.value)">{~TS:ApprovalWorkflow-Bindings-Option:Record.Options~}</select>
						</div>
					</td>
				</tr>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-Hash',
			Template: /*html*/`<div class="apwb-doc-hash">{~D:Record.DocHash~}</div>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-DefaultTag',
			Template: /*html*/`<span class="apwb-tag default">fallback</span><span class="apw-help" role="button" tabindex="0" title="What's this?" onclick="ApprovalWorkflowHelp.show('bindings.default', this)"><i class="fas fa-circle-info"></i></span>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-OverrideTag',
			Template: /*html*/`<span class="apwb-tag override">overrides default</span>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-OptOutTag',
			Template: /*html*/`<span class="apwb-tag optout">no approval · override</span>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-InheritedTag',
			Template: /*html*/`<span class="apwb-tag inherited">inherited</span>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-OrphanTag',
			Template: /*html*/`<span class="apwb-tag orphan">not in document list</span>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-Option',
			Template: /*html*/`<option value="{~D:Record.Hash~}" {~D:Record.SelectedAttr~}>{~D:Record.Label~}</option>`
		},
		{
			Hash: 'ApprovalWorkflowBindings-Actions-Template',
			Template: /*html*/`
				{~TS:ApprovalWorkflowBindings-Actions-ReadOnly:Record.ReadOnlySlot~}
				{~TS:ApprovalWorkflowBindings-Actions-Dirty:Record.DirtySlot~}
				{~TS:ApprovalWorkflowBindings-Actions-Save:Record.EditSlot~}
				{~TS:ApprovalWorkflowBindings-Actions-Blocked:Record.BlockedSlot~}`
		},
		{
			Hash: 'ApprovalWorkflowBindings-Actions-Blocked',
			Template: /*html*/`<span title="{~D:Record.Reason~}"><button class="button is-small is-success" disabled><span class="icon is-small"><i class="fas fa-floppy-disk"></i></span><span>{~D:Record.SaveLabel~}</span></button></span>`
		},
		{
			Hash: 'ApprovalWorkflowBindings-Actions-ReadOnly',
			Template: /*html*/`<span class="apwb-badge" title="Editing requires customer-admin permission."><span class="icon is-small"><i class="fas fa-eye"></i></span> Read only</span>`
		},
		{
			Hash: 'ApprovalWorkflowBindings-Actions-Dirty',
			Template: /*html*/`<span class="apwb-dirty"><span class="icon is-small"><i class="fas fa-circle"></i></span> Unsaved changes</span>`
		},
		{
			Hash: 'ApprovalWorkflowBindings-Actions-Save',
			Template: /*html*/`<button class="button is-small is-success" onclick="_Pict.views.ApprovalWorkflowBindings.saveBindings()"><span class="icon is-small"><i class="fas fa-floppy-disk"></i></span><span>{~D:Record.SaveLabel~}</span></button>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-ServerPending',
			Template: /*html*/`<div class="notification is-warning is-soft py-2 px-3 mb-3"><span class="icon"><i class="fas fa-triangle-exclamation"></i></span> <span>You can configure these bindings now, but the approvals service does not yet enforce <strong>observation</strong> approvals — they take effect once server support ships.</span></div>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-ChooseProject',
			Template: /*html*/`<div class="apwb-empty has-text-grey has-text-centered"><span class="icon is-large"><i class="fas fa-folder-open fa-2x"></i></span><p class="mt-3">Choose a project from the scope bar above to view or override its bindings.</p></div>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-Empty',
			Template: /*html*/`<div class="apwb-empty has-text-grey has-text-centered"><span class="icon is-large"><i class="fas fa-link fa-2x"></i></span><p class="mt-3">No {~D:AppData.ApprovalWorkflowEditor.BindingsTargetNounPlural~} found for your organization.</p></div>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-Loading',
			Template: /*html*/`<div class="apwb-empty has-text-grey has-text-centered"><span class="apwb-spinner"></span><p class="mt-3">Loading document bindings…</p></div>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-ScopeLoading',
			Template: /*html*/`<div class="apwb-empty has-text-grey has-text-centered"><span class="apwb-spinner"></span><p class="mt-3">Loading project bindings…</p></div>`
		},
		{
			Hash: 'ApprovalWorkflow-Bindings-Error',
			Template: /*html*/`<div class="notification is-danger is-soft py-2 px-3 mb-3"><span class="icon"><i class="fas fa-triangle-exclamation"></i></span> <span>{~D:Record.Message~}</span></div>`
		}
	],

	Renderables:
	[
		{ RenderableHash: 'ApprovalWorkflowBindingsWrap', TemplateHash: 'ApprovalWorkflow-Bindings-Wrap', ContentDestinationAddress: '#PlatformConfiguration-Application-Container' }
	]
};
